import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { TopPanelMenuComponent } from "../../../components/TopPanelMenu";
import { UserSettingsDesktop } from "../../../components/UserSettings/UserSettings";
import { AUTH_STORED_ROUTE } from "../../../constants/localStorageKeys";
import { useAuth } from "../../../hooks/useAuth";
import {
  findActiveReportsTab,
  getVisibleReportsTabs,
} from "../../../routes/reports.routes";
import { isAdminUser } from "../../../utils/isAdminUser";
import { safeStorage } from "../../../utils/storage";
import {
  ContentContainer,
  NavContentContainer,
  TopPanelItemsContainerDesktop,
  UnauthorizedContainer,
  UnauthorizedTitle,
  UnauthorizedText,
} from "./ReportsLayout.styles";

/**
 * Layout for the /reports section (EN-5129 — formerly mounted at /admin).
 * Hosts the reports dashboard and its top-tab navigation. The Retool
 * replacement pages live in the separate, SuperAdmin-gated /admin section
 * (see AdminLayout).
 */
export function ReportsLayout() {
  const { t } = useTranslation();

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!isAuthenticated) {
    safeStorage.setItem(
      AUTH_STORED_ROUTE,
      `${location.pathname}${location.search}`
    );
    return <Navigate to="/login" />;
  }

  if (!isAdminUser()) {
    return (
      <UnauthorizedContainer>
        <UnauthorizedTitle>
          {t("reports_accessDenied_layoutTitle")}
        </UnauthorizedTitle>
        <UnauthorizedText>
          {t("reports_accessDenied_layoutBody")}
        </UnauthorizedText>
      </UnauthorizedContainer>
    );
  }

  const activeTab = findActiveReportsTab(location.pathname);
  const visibleTabs = getVisibleReportsTabs();

  const handleLogoClick = () => {
    navigate("/reports");
  };

  const handleTabClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    path: string
  ) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <>
      <TopPanelMenuComponent onLogoClick={handleLogoClick}>
        <NavContentContainer>
          <TopPanelItemsContainerDesktop>
            {visibleTabs.map((tab) => (
              <a
                key={tab.id}
                href={tab.path}
                onClick={(e) => handleTabClick(e, tab.path)}
                className={activeTab.id === tab.id ? "active" : ""}
              >
                {tab.labelKey ? t(tab.labelKey) : tab.name}
              </a>
            ))}
          </TopPanelItemsContainerDesktop>
        </NavContentContainer>

        <UserSettingsDesktop />
      </TopPanelMenuComponent>

      <ContentContainer sidebarState="hidden">
        <Outlet />
      </ContentContainer>
    </>
  );
}
